import { X } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";

const services = [
  {
    title: "Tôlerie et peinture",
    desc: "Accident, accrochage, bosses, rayures et grêle : redressage, remplacement d'éléments et peinture en cabine. 2 à 3 jours ouvrables une fois l'accord et les pièces reçus.",
    href: "/services/tolerie-peinture",
    detail: (
      <>
        <p className="mb-4">
          Qu'il s'agisse d'un accrochage sur parking, d'un choc avant ou arrière, d'une bosse, d'une rayure profonde ou de dégâts de grêle, notre atelier redonne à votre véhicule son état d'origine. Les bosses se réparent chez nous par redressage puis peinture de l'élément.
        </p>
        <p className="font-semibold text-foreground mb-3">Notre processus en 5 étapes :</p>
        <div className="space-y-4">
          {[
            ["1. Photos & devis", "Inspection des éléments endommagés, photos pour votre assurance et devis détaillé poste par poste, gratuit et sans engagement."],
            ["2. Assurance & pièces", "Nous transmettons le devis à votre assureur et commandons les pièces dès son accord."],
            ["3. Tôlerie", "Redressage des éléments déformés ou remplacement des pièces trop endommagées par des pièces constructeur neuves."],
            ["4. Peinture en cabine", "Teinte mesurée au spectrophotomètre, peinture en cabine pressurisée : base, effet métallisé ou nacré, puis vernis."],
            ["5. Contrôle & restitution", "Remontage, contrôle qualité et nettoyage. On vous rend la voiture là où on l'a prise."],
          ].map(([title, text]) => (
            <div key={title}>
              <p className="font-semibold text-foreground text-xs uppercase tracking-wider mb-1">{title}</p>
              <p>{text}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 font-semibold text-foreground">Durée : 2 à 3 jours ouvrables une fois l'accord de l'assurance et les pièces reçus</p>
      </>
    ),
  },
  {
    title: "Pare-brise",
    desc: "Impact ou fissure : réparation ou remplacement du pare-brise. Le bris de glace est souvent couvert par la casco partielle.",
    href: "/services/pare-brise",
    detail: (
      <>
        <p className="mb-4">Un gravillon, une fissure qui s'étend avec le froid, un pare-brise touché lors d'un accident : nous réparons ou remplaçons votre pare-brise, toutes marques.</p>
        <p className="font-semibold text-foreground mb-2">Ce que nous traitons :</p>
        <ul className="list-disc list-inside space-y-1 mb-4">
          <li>Impacts de gravillon</li>
          <li>Fissures</li>
          <li>Pare-brise endommagé lors d'un accident ou par vandalisme</li>
        </ul>
        <p className="mb-4">Le bris de glace est souvent couvert par la casco partielle, selon votre contrat. Nous faisons la déclaration et suivons le dossier avec votre assurance.</p>
        <p className="font-semibold text-foreground">Nous venons chercher la voiture dans tout le canton de Genève, et on vous la rend là où on l'a prise.</p>
      </>
    ),
  },
  {
    title: "Gestion du sinistre",
    desc: "Nous traitons tout le dossier avec votre assurance. Vous ne payez que votre franchise. Véhicule de courtoisie dès CHF 49.–/jour.",
    href: "/services/gestion-sinistre",
    detail: (
      <>
        <p className="mb-4">Après un accident, une bosse de parking, la grêle ou un bris de glace, nous nous occupons de toutes les démarches avec votre assurance : AXA, Zurich, Allianz, Helvetia, Generali, Mobilière, Vaudoise, Baloise et les autres.</p>
        <ul className="list-disc list-inside space-y-1 mb-4">
          <li>Devis et photos envoyés à votre assureur</li>
          <li>Suivi du dossier jusqu'à l'accord de prise en charge</li>
          <li>Facturation directe à l'assurance : vous ne payez que votre franchise</li>
          <li>Véhicule de courtoisie dès CHF 49.– par jour</li>
        </ul>
        <p className="font-semibold text-foreground">Prise en charge dans tout le canton de Genève : on vous rend la voiture là où on l'a prise.</p>
      </>
    ),
  },
];

const ServicesSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const handleOpen = (i: number) => {
    setOpenIndex(i);
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleClose = () => setOpenIndex(null);
  const expanded = openIndex !== null ? services[openIndex] : null;

  return (
    <section id="services" className="py-24 relative">

      <div className="container mx-auto px-4">
        <div className="mb-12 max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Nos services</h2>
          <p className="text-muted-foreground">
            Toutes marques, pièces d'origine en priorité. Le devis est gratuit.
          </p>
        </div>

        <div ref={ref} className="relative">
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-500 ${
              openIndex !== null ? "opacity-0 scale-95 pointer-events-none absolute inset-0" : "opacity-100 scale-100"
            }`}
          >
            {services.map((s, i) => (
              <div
                key={s.title}
                className="glass-card rounded-lg p-6"
              >
                <h3 className="font-semibold text-lg mb-3 tracking-tight">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                <button
                  onClick={() => handleOpen(i)}
                  className="mt-4 flex items-center gap-1.5 text-primary text-sm font-medium hover:underline transition-all cursor-pointer"
                >
                  En savoir plus
                </button>
              </div>
            ))}
          </div>

          {expanded && (
            <div className="animate-fade-in-up" style={{ animationDuration: "0.4s" }}>
              <div className="glass-card rounded-lg overflow-hidden">
                <div className="flex items-start justify-between p-6 pb-4 border-b border-border">
                  <div className="flex items-center gap-4">
                    <div>
                      <h3 className="font-bold text-xl tracking-tight">{expanded.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{expanded.desc}</p>
                    </div>
                  </div>
                  <button onClick={handleClose} className="p-2 rounded-lg hover:bg-muted transition-colors shrink-0 cursor-pointer" aria-label="Fermer">
                    <X className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>
                <div className="p-6 text-sm text-muted-foreground leading-relaxed max-h-[60vh] overflow-y-auto">
                  {expanded.detail}
                </div>
                <div className="p-6 pt-2 border-t border-border">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a href="#contact" className="inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-primary/90 active:scale-95 transition-all">
                      Demander un devis
                    </a>
                    <Link to={expanded.href} className="inline-flex items-center justify-center rounded-lg border border-primary/30 text-primary px-6 py-3 text-sm font-medium hover:bg-primary/10 transition-colors">
                      Voir la page du service
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
